/**
 * Canal d'un document DÉRIVÉ — le BL né d'une commande, la facture née d'un BL,
 * l'avoir né d'une facture.
 *
 * Le canal dit d'où vient la VENTE : boutique en ligne, caisse, Messenger…
 * Cette origine ne change pas quand le document se transforme — le BL d'une
 * commande boutique reste une vente en ligne, et sa facture aussi. Sans cette
 * reprise, tout enfant retombait sur le défaut de création ('local') et la
 * colonne « Canal » annonçait « Local » sur des documents nés de la boutique,
 * alors que la ligne client, elle, affichait « Boutique en ligne ».
 *
 * Facturation groupée : plusieurs parents. Le canal n'est repris que s'ils
 * s'accordent TOUS. Une facture qui regroupe un BL boutique et un BL comptoir
 * n'a pas d'origine unique : elle retombe sur 'local' plutôt que d'hériter
 * arbitrairement du premier BL de la sélection.
 */

/** Canal par défaut : la vente prise au comptoir, sans canal distant. */
export const DEFAULT_SALES_CHANNEL = 'local';

export function inheritSalesChannel(
  parentChannels: ReadonlyArray<string | null | undefined>,
): string {
  const distinct = new Set(
    parentChannels
      .map((channel) => channel?.trim())
      .filter((channel): channel is string => Boolean(channel)),
  );
  if (distinct.size !== 1) return DEFAULT_SALES_CHANNEL;
  return [...distinct][0] ?? DEFAULT_SALES_CHANNEL;
}

/**
 * Physical store inherited by a derived document.
 *
 * A store is inherited only when every parent has the same non-null origin.
 * Mixed-store grouped invoices and central online orders deliberately remain
 * company-level (`null`) instead of being attributed to an arbitrary branch.
 */
export function inheritSalesOriginStore(
  parentStoreIds: ReadonlyArray<string | null | undefined>,
): string | null {
  if (parentStoreIds.length === 0) return null;
  const normalized = parentStoreIds.map((storeId) => storeId?.trim() || null);
  if (normalized.some((storeId) => storeId === null)) return null;
  const distinct = new Set(normalized as string[]);
  return distinct.size === 1 ? ([...distinct][0] ?? null) : null;
}
